import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
  Input,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const DIRECTION_OPTIONS = [
  { label: "Buy", value: 1 },
  { label: "Sell", value: 2 },
];

const DATE_FORMAT_DISPLAY = "DD MMMM YYYY";
const DATE_FORMAT_API = "DD MMMM YYYY";
const LOCALE = "en";
const DATE_FORMAT_LOCALE = "dd MMMM yyyy";

// Converts [2026, 3, 30] → dayjs object for DatePicker
const parseDateArray = (val) => {
  if (Array.isArray(val) && val.length >= 3) {
    const [y, m, d] = val;
    return dayjs(
      `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    );
  }
  // Fallback: plain string date
  if (typeof val === "string" && val) return dayjs(val, DATE_FORMAT_DISPLAY);
  return null;
};

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
    </Tooltip>
  </span>
);

const AddTrade = ({ userData }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [counterpartyOptions, setCounterpartyOptions] = useState([]);
  const [securityOptions, setSecurityOptions] = useState([]);

  const { session } = userData || {};
  const navigate = useNavigate();
  const location = useLocation();

  const tradeId = location.state || null;
  const isEdit = !!tradeId;

  useEffect(() => {
    fetchTemplate();
  }, []);

  // Fetch detail only after template is loaded so dropdowns are ready
  useEffect(() => {
    if (isEdit) fetchTradeDetail(tradeId);
  }, [tradeId]);

  // ── Template ──────────────────────────────────────────────────────────────
  const fetchTemplate = () => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTrades}/trades/template`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load trade template.",
        );
        setIsLoading(false);
        return;
      }

      if (Array.isArray(res?.portfolios)) {
        setPortfolioOptions(
          res.portfolios.map((p) => ({
            label: p?.portfolioName,
            value: p?.portfolioId,
          })),
        );
      }

      if (Array.isArray(res?.counterparties)) {
        setCounterpartyOptions(
          res.counterparties.map((c) => ({
            label: c?.bankName,
            value: c?.id,
          })),
        );
      }

      if (Array.isArray(res?.securities)) {
        setSecurityOptions(
          res.securities.map((s) => ({
            label: s?.instrumentName,
            value: s?.id,
          })),
        );
      }

      setIsLoading(false);
    });
  };

  // ── Trade detail (edit mode only) ─────────────────────────────────────────
  const fetchTradeDetail = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTrades}/trades/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load trade details.",
        );
        setIsLoading(false);
        return;
      }

      form.setFieldsValue({
        tradeReference: res.tradeReference,
        direction: res.direction ?? null,
        portfolioId: res.portfolioId ?? null,
        counterpartyId: res.counterpartyId ?? null,
        securityId: res.securityId ?? null,
        faceValue: res.faceValue ?? null,
        price: res.price ?? null,
        yield: res.yield ?? null,
        tradeDate: parseDateArray(res.tradeDate),
        settlementDate: parseDateArray(res.settlementDate),
      });

      setIsLoading(false);
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const onFinish = (values) => {
    const body = {
      method: isEdit ? "PUT" : "POST",
      tradeReference: values.tradeReference,
      portfolioId: values.portfolioId,
      counterpartyId: values.counterpartyId,
      securityId: values.securityId,
      direction: values.direction,
      faceValue: values.faceValue,
      price: values.price,
      yield: values.yield,
      tradeDate: values.tradeDate?.format(DATE_FORMAT_API),
      settlementDate: values.settlementDate?.format(DATE_FORMAT_API),
      locale: LOCALE,
      dateFormat: DATE_FORMAT_LOCALE,
      session,
      ...(isEdit && { tradeId }),
    };

    const url = isEdit
      ? `${apiConfig.GetTrades}/trades/${tradeId}`
      : `${apiConfig.GetTrades}/trades`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage ||
            (isEdit ? "Failed to update trade." : "Failed to create trade."),
        );
        setIsLoading(false);
        return;
      }

      BOM.AlertMsg(
        cnt.SUCCESS,
        isEdit ? "Trade updated successfully." : "Trade created successfully.",
      );
      setIsLoading(false);
      navigate(-1);
    });
  };

  return (
    <MainLayout pageItems={staticConfig.manageTrade}>
      <TopContent pageTitle={labelConfig.trade} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              {isEdit ? "Edit Trade" : "Create Trade"}
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* ── Trade Reference & Direction ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Trade Reference"
                        tip="A unique reference code for this trade (e.g. TRD-2026-00001)."
                      />
                    }
                    name="tradeReference"
                    rules={[
                      {
                        required: true,
                        message: "Trade reference is required",
                      },
                    ]}
                  >
                    <Input
                      style={{ height: 44 }}
                      placeholder="e.g. TRD-2026-00001"
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Direction"
                        tip="Whether you are buying or selling the instrument."
                      />
                    }
                    name="direction"
                    rules={[
                      { required: true, message: "Direction is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={DIRECTION_OPTIONS}
                      placeholder="Select Direction"
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Portfolio & Counterparty ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Portfolio"
                        tip="The portfolio to which this trade is assigned."
                      />
                    }
                    name="portfolioId"
                    rules={[
                      { required: true, message: "Portfolio is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={portfolioOptions}
                      placeholder="Select Portfolio"
                      showSearch
                      optionFilterProp="label"
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Counterparty"
                        tip="The counterparty on the other side of this trade."
                      />
                    }
                    name="counterpartyId"
                    rules={[
                      { required: true, message: "Counterparty is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={counterpartyOptions}
                      placeholder="Select Counterparty"
                      showSearch
                      optionFilterProp="label"
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Security ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Security Instrument"
                        tip="The underlying security instrument for this trade."
                      />
                    }
                    name="securityId"
                    rules={[
                      { required: true, message: "Security is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={securityOptions}
                      placeholder="Select Security"
                      showSearch
                      optionFilterProp="label"
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Face Value & Price ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Face Value"
                        tip="The nominal / par value of the instrument being traded."
                      />
                    }
                    name="faceValue"
                    rules={[
                      { required: true, message: "Face value is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Face value must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 1,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      // precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Price"
                        tip="The price at which the instrument is traded."
                      />
                    }
                    name="price"
                    rules={[
                      { required: true, message: "Price is required" },
                      {
                        type: "number",
                        min: 0,
                        message: "Price must be 0 or greater",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 8.00"
                      // precision={7}
                      min={0}
                      formatter={(value) =>
                        value
                          ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                          : ""
                      }
                      parser={(value) => value?.replace(/,/g, "")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Yield & Trade Date ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Yield"
                        tip="The annualised yield of the instrument at the traded price."
                      />
                    }
                    name="yield"
                    rules={[
                      { required: true, message: "Yield is required" },
                      {
                        type: "number",
                        min: 0,
                        message: "Yield must be 0 or greater",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 11.34678"
                      // precision={5}
                      min={0}
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Trade Date"
                        tip="The date on which the trade is executed."
                      />
                    }
                    name="tradeDate"
                    rules={[
                      { required: true, message: "Trade date is required" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format={DATE_FORMAT_DISPLAY}
                      placeholder="DD MMMM YYYY"
                      disabled={isEdit}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Settlement Date ── */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Date"
                        tip="The date on which the trade settles."
                      />
                    }
                    name="settlementDate"
                    rules={[
                      {
                        required: true,
                        message: "Settlement date is required",
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format={DATE_FORMAT_DISPLAY}
                      placeholder="DD MMMM YYYY"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    {isEdit ? "Update" : "Submit"}
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

export default connect(mapStateToProps, { rightAction })(AddTrade);
